import { useLocationsStore } from '@/store/locations-store';
import { ref, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { useServicesStore } from '@/store/services-store';
import { storeToRefs } from 'pinia';
import { Collection } from '@/store/common/collections';
import vendorsService from '@/services/vendors.service';
import { useTimetablesStore } from '@/store/timetables-store';
import { useVendorsStore } from '@/store/vendors-store';
import { useUsersStore } from '@/store/users-store';
import { VendorSettingsPage } from '@/model/vendor-settings-page';
import { Vendor } from '@/model/vendor';

// Chargé uniquement 1x, lors de l'ouverture de l'app
const onboardingStarted = ref(false);
const watchStopHandleRouteChange = ref();
const watchStopHandleLocations = ref();
const watchStopHandleServices = ref();
const watchStopHandleTimetables = ref();
const watchStopHandleOnboardingComplete = ref();
const showOnboardingForCurrentRoute = ref(false);
const vendorHasAtLeastOneLocation = ref(false);
const vendorHasAtLeastOneService = ref(false);
const vendorHasAtLeastOneTimetable = ref(false);
const sidebarItemToHighlight = ref('');
const isLoading = ref(false);
const onboardingComplete = ref(false);

export function useOnboarding() {
  // On a besoin de les initialiser ici, après l'importation du composant pour avoir accès à Pinia et au Router
  const router = useRouter();
  const servicesStore = storeToRefs(useServicesStore());
  const locationsStore = storeToRefs(useLocationsStore());
  const timetablesStore = storeToRefs(useTimetablesStore());

  watchEffect(() => {
    onboardingComplete.value =
      vendorHasAtLeastOneTimetable.value &&
      vendorHasAtLeastOneLocation.value &&
      vendorHasAtLeastOneService.value;
  });

  async function start() {
    if (onboardingStarted.value === false) {
      isLoading.value = true;
      onboardingStarted.value = true;

      // Initialisation des variables & watchers, 1 seule fois
      watchStopHandleRouteChange.value = watch(router.currentRoute, () => {
        handleOnboardingVisibilityBasedOnCurrentRoute();
      });
      watchStopHandleLocations.value = watch(locationsStore.entities, () => {
        checkIfVendorHasLocations();
        handleOnboardingVisibilityBasedOnCurrentRoute();
        setSidebarItemToHighlight();
      });
      watchStopHandleServices.value = watch(servicesStore.entities, () => {
        checkIfVendorHasServices();
        handleOnboardingVisibilityBasedOnCurrentRoute();
        setSidebarItemToHighlight();
      });
      watchStopHandleTimetables.value = watch(
        timetablesStore.entities,
        async () => {
          await checkIfVendorHasTimetables();
          handleOnboardingVisibilityBasedOnCurrentRoute();
          setSidebarItemToHighlight();
        },
      );
      watchStopHandleOnboardingComplete.value = watch(
        onboardingComplete,
        async () => {
          if (onboardingComplete.value) {
            const vendorsStore = useVendorsStore();
            const usersStore = useUsersStore();
            const vendor: Vendor = vendorsStore.getById(
              usersStore.loggedInVendorId,
            );
            await vendorsStore.edit(
              {
                id: vendor.id,
                onboarding_completed: true,
              },
              VendorSettingsPage.PUBLIC,
              false,
            );
            watchStopHandleRouteChange.value();
            watchStopHandleOnboardingComplete.value();
          }
        },
      );

      // Lancement des vérifications
      await checkIfVendorHasTimetables();
      checkIfVendorHasLocations();
      checkIfVendorHasServices();
      handleOnboardingVisibilityBasedOnCurrentRoute();
      setSidebarItemToHighlight();
      isLoading.value = false;
    }
  }

  function handleOnboardingVisibilityBasedOnCurrentRoute() {
    if (
      [
        'MyAccountEmployeeEdit',
        'MyAccountEmployeeSettings',
        'MyAccountEmployeeIntegrations',
      ].includes(router.currentRoute.value.name as string)
    ) {
      showOnboardingForCurrentRoute.value = false;
    } else if (
      (router.currentRoute.value.name as string) !== 'Locations' &&
      !vendorHasAtLeastOneLocation.value
    ) {
      showOnboardingForCurrentRoute.value = true;
    } else if (
      (router.currentRoute.value.name as string) !== 'Services' &&
      vendorHasAtLeastOneLocation.value &&
      !vendorHasAtLeastOneService.value
    ) {
      showOnboardingForCurrentRoute.value = true;
    } else if (
      (router.currentRoute.value.name as string) !== 'Timetables' &&
      vendorHasAtLeastOneLocation.value &&
      vendorHasAtLeastOneService.value &&
      !vendorHasAtLeastOneTimetable.value
    ) {
      showOnboardingForCurrentRoute.value = true;
    } else {
      showOnboardingForCurrentRoute.value = false;
    }
  }

  function setSidebarItemToHighlight() {
    if (!vendorHasAtLeastOneLocation.value) {
      sidebarItemToHighlight.value = Collection.LOCATIONS;
    } else if (!vendorHasAtLeastOneService.value) {
      sidebarItemToHighlight.value = Collection.SERVICES;
    } else if (!vendorHasAtLeastOneTimetable.value) {
      sidebarItemToHighlight.value = Collection.TIMETABLES;
    } else {
      sidebarItemToHighlight.value = '';
    }
  }

  async function checkIfVendorHasTimetables() {
    vendorHasAtLeastOneTimetable.value =
      (await vendorsService.findTimetablesCount()) > 0;
    if (vendorHasAtLeastOneTimetable.value === true) {
      watchStopHandleTimetables.value();
    }
  }

  function checkIfVendorHasLocations() {
    vendorHasAtLeastOneLocation.value =
      locationsStore.entities.value.length > 0;
    if (vendorHasAtLeastOneLocation.value === true) {
      watchStopHandleLocations.value();
    }
  }

  function checkIfVendorHasServices() {
    vendorHasAtLeastOneService.value = servicesStore.entities.value.length > 0;
    if (vendorHasAtLeastOneService.value === true) {
      watchStopHandleServices.value();
    }
  }

  return {
    vendorHasAtLeastOneLocation,
    vendorHasAtLeastOneService,
    vendorHasAtLeastOneTimetable,
    showOnboardingForCurrentRoute,
    sidebarItemToHighlight,
    onboardingComplete,
    loadingOnboarding: isLoading,
    start,
    onboardingStarted,
  };
}
